import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

interface GlobalState {
  [key: string]: unknown;
}

interface GlobalContextType {
  state: GlobalState;
  setState: (key: string, value: unknown) => void;
  removeState: (key: string) => void;
  clearState: () => void;
}

const GlobalContext = createContext<GlobalContextType | null>(null);

export function GlobalProvider({ children }: { children: ReactNode }) {
  const [state, setStateObj] = useState<GlobalState>({});

  const setState = useCallback((key: string, value: unknown) => {
    setStateObj((prev) => ({ ...prev, [key]: value }));
  }, []);

  const removeState = useCallback((key: string) => {
    setStateObj((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const clearState = useCallback(() => {
    setStateObj({});
  }, []);

  // Console log on every state change
  useEffect(() => {
    console.log('%c[GlobalContext] State Updated:', 'color: #1976d2; font-weight: bold;', state);
  }, [state]);

  return (
    <GlobalContext.Provider value={{ state, setState, removeState, clearState }}>
      {children}
    </GlobalContext.Provider>
  );
}

export function useGlobalContext() {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobalContext must be used within GlobalProvider');
  }
  return context;
}
